/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC } from 'react'
import useTranslation from 'next-translate/useTranslation'
import cn from 'clsx'
import dynamic from 'next/dynamic'
import BagIcon from '@components/icons/BagIcon'
import { useUI } from '@components/ui/context'
import Button from '@components/ui/Button'
import { formatWalletAccount } from '@lib/web3/helpers/formatWalletAccount'
import { MenuIcon } from '@components/icons/MenuIcon'
import { CurrencySelectButton } from '@components/web3/CurrencySelectButton'
import { useCryptoCurrencyContext } from '@lib/web3'
import { sendEcommerceEvent, sendEvent } from '@lib/tracking'
import {
  getProductCategoriesFromFacetValues,
  getProductOtherCategoriesFromFacetValues,
  useCart,
  useCustomer,
} from '@lib/api'
import { isSuccess } from '@lib/api/utils'

const ConnectCTA = dynamic(
  async () => (await import('../Navbar/ConnectCTA')).ConnectCTA
)

interface Props {
  className?: string
}

const UserNav: FC<Props> = ({ className }) => {
  const { t } = useTranslation()
  const cartData = useCart()
  const customerData = useCustomer()
  const { selectedCoin } = useCryptoCurrencyContext()
  const { toggleSidebar, setSidebarView } = useUI()

  const walletId =
    (isSuccess(customerData) && customerData.data.walletId) || null

  const walletButtonText =
    walletId !== null
      ? formatWalletAccount(walletId)
      : t('common:connect_wallet')

  const handleCartClick = () => {
    setSidebarView('CART_VIEW')
    toggleSidebar()
    if (isSuccess(cartData) && cartData.data) {
      const items = cartData.data.lines

      sendEcommerceEvent({
        event: 'view_cart',
        currency: 'USD',
        value: cartData.data.subTotal,
        items: items.map((item) => ({
          item_id: item.id,
          item_name: item.productVariant.product.name,
          currency: 'USD',
          index: item.id,
          item_brand: item.productVariant.product.brandName ?? '',
          item_category: getProductCategoriesFromFacetValues(
            item.productVariant.product.facetValues
          ).join(', '),
          item_category2: getProductOtherCategoriesFromFacetValues(
            item.productVariant.product.facetValues
          ).join(', '),
          item_variant: item.productVariant.name,
          price: item.productVariant.price / 100,
          quantity: item.quantity,
        })),
      })
    }
  }

  return (
    <nav className={cn('relative flex items-center', className)}>
      <ul className="flex flex-row items-center justify-items-end h-full space-x-6 lg:space-x-16">
        <li
          onClick={() => {
            sendEvent({
              event: 'Click_Header',
              data: {
                click_url: window.location.pathname,
                click_text: 'MY CURRENCY',
                page_path: window.location.pathname,
                button_id: 'header_currency',
              },
            })
          }}
        >
          <CurrencySelectButton selectedCoin={selectedCoin} />
        </li>
        <li
          className="hidden lg:block"
          onClick={() => {
            sendEvent({
              event: 'Click_Header',
              data: {
                click_url: window.location.pathname,
                click_text: walletButtonText,
                page_path: window.location.pathname,
                button_id: `header_wallet_${
                  walletId !== null ? 'connected' : 'not_connected'
                }`,
              },
            })
          }}
        >
          <ConnectCTA />
        </li>
        <li
          onClick={() =>
            sendEvent({
              event: 'Click_Header',
              data: {
                click_url: window.location.pathname,
                click_text: 'CART',
                page_path: window.location.pathname,
                button_id: 'header_cart',
              },
            })
          }
        >
          <Button
            variant="naked"
            onClick={() => {
              handleCartClick()
            }}
            aria-label={`${t('common:nav_cart_name').toLocaleLowerCase()}: ${
              isSuccess(cartData) && cartData.data
                ? cartData.data.totalQuantity
                : '0'
            }`}
          >
            <BagIcon className="mr-2" />
            <span className="hidden lg:block">{t('common:nav_cart_name')}</span>
            (
            {isSuccess(cartData) && cartData.data
              ? cartData.data.totalQuantity
              : '0'}
            )
          </Button>
        </li>
        <li
          onClick={() =>
            sendEvent({
              event: 'Click_Header',
              data: {
                click_url: window.location.pathname,
                click_text: 'burger icon',
                page_path: window.location.pathname,
                button_id: 'header_burger',
              },
            })
          }
        >
          <Button
            variant="naked"
            onClick={() => {
              setSidebarView('MOBILEMENU_VIEW')
              toggleSidebar()
            }}
            aria-label={t('common:nav_menu_name').toLocaleLowerCase()}
          >
            <MenuIcon />
          </Button>
        </li>
      </ul>
    </nav>
  )
}

export default UserNav
